import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCalendar, FiFileText } from 'react-icons/fi';
import {
    AccountTableCard,
    ActivationFunnel,
    AnalyticsFiltersBar,
    FeaturesPanel,
    MetricCard,
    TrendChart,
    UsagePanel,
    buildActivationCards,
    buildOverviewCards,
    buildSetupCards,
    type TrendMetric,
} from '../../components/analytics';
import {
    NEVER_ACCESSED_COLUMNS,
    NEVER_ACCESSED_FORMATTERS,
    SETUP_COLUMNS,
    SETUP_FORMATTERS,
} from '../../components/analytics/accountTables';
import { DEFAULT_FILTERS, DEFAULT_TIMEZONE, GUIA_METRICAS_URL, isQueryable, todayInZone } from '../../components/analytics/analyticsPage';
import { formatDayBR, formatInstantDayBR } from '../../components/analytics/format';
import {
    analyticsApi,
    buildAnalyticsQuery,
    type AcquisitionChurnTrendPoint,
    type AnalyticsFilters,
    type AnalyticsMeta,
    type AnalyticsOverview,
    type RevenueBaseTrendPoint,
} from '../../services/analytics';
import { AdminLayout } from './AdminLayout';
import styles from './AdminAnalytics.module.css';


type TabId = 'overview' | 'usage' | 'features';

const TABS: Array<{ id: TabId; label: string }> = [
    { id: 'overview', label: 'Visão geral' },
    { id: 'usage', label: 'Uso e retenção' },
    { id: 'features', label: 'Funcionalidades e flags' },
];

type RevenueMetric = 'mrr' | 'arr' | 'pagantesAtivas' | 'contasComAcesso' | 'contasTotais' | 'crescimentoPagantesPct' | 'variacaoPagantes';
type FlowMetric = 'novasContas' | 'cancelamentos' | 'churnPct';

const REVENUE_METRICS: TrendMetric<RevenueMetric>[] = [
    { key: 'mrr', label: 'MRR', unit: 'brl' },
    { key: 'arr', label: 'ARR', unit: 'brl' },
    { key: 'pagantesAtivas', label: 'Pagantes ativas', unit: 'int' },
    { key: 'contasComAcesso', label: 'Contas com acesso', unit: 'int' },
    { key: 'contasTotais', label: 'Contas totais', unit: 'int' },
    { key: 'crescimentoPagantesPct', label: 'Crescimento de pagantes', unit: 'pct', allowNegative: true },
    { key: 'variacaoPagantes', label: 'Variação de pagantes', unit: 'int', allowNegative: true },
];

const FLOW_METRICS: TrendMetric<FlowMetric>[] = [
    { key: 'novasContas', label: 'Novas contas', unit: 'int' },
    { key: 'cancelamentos', label: 'Cancelamentos', unit: 'int' },
    { key: 'churnPct', label: 'Churn', unit: 'pct' },
];

export function AdminAnalytics() {
    const [tab, setTab] = useState<TabId>('overview');
    const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_FILTERS);
    const [meta, setMeta] = useState<AnalyticsMeta | null>(null);
    const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
    const [settled, setSettled] = useState<{ key: string; error: string | null } | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [revenueMetric, setRevenueMetric] = useState<RevenueMetric>('mrr');
    const [flowMetric, setFlowMetric] = useState<FlowMetric>('novasContas');

    const timeZone = meta?.timezone ?? DEFAULT_TIMEZONE;
    const today = useMemo(() => todayInZone(timeZone), [timeZone]);

    // Opcoes de filtro: se falhar, o painel segue sem as listas de segmento e faturamento.
    useEffect(() => {
        const controller = new AbortController();
        analyticsApi
            .meta(controller.signal)
            .then(setMeta)
            .catch((e) => {
                if (!controller.signal.aborted) console.error(e);
            });
        return () => controller.abort();
    }, []);

    const queryable = isQueryable(filters);
    const requestKey = queryable ? `${buildAnalyticsQuery(filters)}#${reloadKey}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        analyticsApi
            .overview(filters, controller.signal)
            .then((data) => {
                setOverview(data);
                setSettled({ key: requestKey, error: null });
            })
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar o painel.',
                });
            });
        return () => controller.abort();
        // requestKey resume os filtros e o contador de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const error = settled && settled.key === requestKey ? settled.error : null;

    const handleFiltersChange = useCallback((next: AnalyticsFilters) => setFilters(next), []);

    const cards = useMemo(
        () => (overview ? buildOverviewCards(overview.cards, overview.filtroDeStatus) : null),
        [overview],
    );
    const activationCards = useMemo(
        () => (overview ? buildActivationCards(overview.planosAtivacao) : null),
        [overview],
    );

    const setupCards = useMemo(() => (overview ? buildSetupCards(overview) : null), [overview]);

    const showSkeleton = loading && !overview;

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <h2 className={styles.pageTitle}>Analytics</h2>
                <div className={styles.headerRight}>
                    <a className={styles.guideLink} href={GUIA_METRICAS_URL} target="_blank" rel="noopener noreferrer">
                        <FiFileText size={14} aria-hidden="true" />
                        Guia das métricas (PDF)
                    </a>
                    <span className={styles.today}>
                        <FiCalendar size={14} aria-hidden="true" />
                        {formatInstantDayBR(new Date().toISOString(), timeZone)}
                    </span>
                </div>
            </div>

            <AnalyticsFiltersBar
                filters={filters}
                onChange={handleFiltersChange}
                segmentos={meta?.filters.segmentos ?? []}
                faturamentos={meta?.filters.faturamentos ?? []}
                totalContas={overview?.cards.contasTotais.value ?? null}
                range={overview ? { from: overview.period.from, to: overview.period.to } : null}
                maxDay={today}
            />

            <div className={styles.tabs} role="tablist" aria-label="Seções do painel">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        id={`analytics-tab-${t.id}`}
                        aria-selected={tab === t.id}
                        aria-controls={`analytics-panel-${t.id}`}
                        className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div
                role="tabpanel"
                id={`analytics-panel-${tab}`}
                aria-labelledby={`analytics-tab-${tab}`}
                className={styles.panel}
            >
                {tab === 'features' && (
                    <FeaturesPanel
                        filters={filters}
                        enabled={queryable}
                        refreshKey={reloadKey}
                        onRetry={() => setReloadKey((k) => k + 1)}
                        timeZone={timeZone}
                        events={meta?.events}
                    />
                )}

                {tab === 'usage' && (
                    <UsagePanel
                        filters={filters}
                        enabled={queryable}
                        refreshKey={reloadKey}
                        onRetry={() => setReloadKey((k) => k + 1)}
                        timeZone={timeZone}
                        events={meta?.events}
                    />
                )}

                {tab === 'overview' && (
                    <>
                        <div className={styles.sectionHeader}>
                            <h3 className={styles.sectionTitle}>Negócio e growth</h3>
                            <p className={styles.sectionSubtitle}>
                                Visão executiva da base, receita recorrente e cancelamentos
                            </p>
                        </div>

                        {!queryable && (
                            <p className={styles.notice}>Informe as duas datas do período personalizado.</p>
                        )}

                        {error && (
                            <div className={styles.error} role="alert">
                                <span>{error}</span>
                                <button type="button" className={styles.retry} onClick={() => setReloadKey((k) => k + 1)}>
                                    Tentar novamente
                                </button>
                            </div>
                        )}

                        {showSkeleton && (
                            <div className={styles.loadingState} aria-live="polite">
                                Carregando...
                            </div>
                        )}

                        {overview && cards && (
                            <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`} aria-busy={loading}>
                                <div className={styles.primaryGrid}>
                                    {cards.primary.map((c, i) => (
                                        <MetricCard
                                            key={c.id}
                                            infoKey={c.id}
                                            title={c.title}
                                            value={c.value}
                                            caption={c.caption}
                                            highlight={c.highlight}
                                            tipAlign={i === 0 ? 'start' : 'end'}
                                        />
                                    ))}
                                </div>

                                <div className={styles.secondaryGrid}>
                                    {cards.secondary.map((c, i) => (
                                        <MetricCard
                                            key={c.id}
                                            infoKey={c.id}
                                            title={c.title}
                                            value={c.value}
                                            caption={c.caption}
                                            tipAlign={i === 0 ? 'start' : 'end'}
                                        />
                                    ))}
                                </div>

                                <div className={styles.chartsGrid}>
                                    <TrendChart<RevenueMetric, RevenueBaseTrendPoint>
                                        title="Tendência de receita e base"
                                        infoKey="tendenciaReceitaBase"
                                        points={overview.trends.receitaBase}
                                        granularity={overview.trends.granularity}
                                        endDay={overview.period.to}
                                        metrics={REVENUE_METRICS}
                                        metric={revenueMetric}
                                        onMetricChange={setRevenueMetric}
                                        getValue={(p, m) => p[m]}
                                        loading={loading}
                                    />
                                    <TrendChart<FlowMetric, AcquisitionChurnTrendPoint>
                                        title="Tendência de aquisição e churn"
                                        infoKey="tendenciaAquisicaoChurn"
                                        points={overview.trends.aquisicaoChurn}
                                        granularity={overview.trends.granularity}
                                        endDay={overview.period.to}
                                        metrics={FLOW_METRICS}
                                        metric={flowMetric}
                                        onMetricChange={setFlowMetric}
                                        getValue={(p, m) => p[m]}
                                        loading={loading}
                                    />
                                </div>

                                <div className={`${styles.sectionHeader} ${styles.sectionGap}`}>
                                    <h3 className={styles.sectionTitle}>Planos e ativação</h3>
                                    <p className={styles.sectionSubtitle}>
                                        Composição da base e avanço até a rotina financeira
                                    </p>
                                </div>

                                {meta?.coverage?.acesso && (
                                    <p className={styles.notice}>
                                        Histórico de acesso desde {formatDayBR(meta.coverage.acesso)}. Funil, Ativação D7
                                        e TTV consideram só as empresas cadastradas a partir dessa data.
                                    </p>
                                )}

                                {activationCards && (
                                    <div className={styles.activationGrid}>
                                        {activationCards.map((c, i) => (
                                            <MetricCard
                                                key={c.id}
                                                infoKey={c.id}
                                                title={c.title}
                                                value={c.value}
                                                caption={c.caption}
                                                tipAlign={i === 0 ? 'start' : 'end'}
                                            />
                                        ))}
                                    </div>
                                )}

                                <ActivationFunnel
                                    funil={overview.funil}
                                    filters={filters}
                                    enabled={queryable}
                                    refreshKey={reloadKey}
                                    timeZone={timeZone}
                                />

                                <div className={`${styles.sectionHeader} ${styles.sectionGap}`}>
                                    <h3 className={styles.sectionTitle}>Setup proposto</h3>
                                    <p className={styles.sectionSubtitle}>Critério exploratório separado da North Star CRFA</p>
                                </div>

                                {setupCards && (
                                    <div className={styles.setupGrid}>
                                        {setupCards.map((c, i) => (
                                            <MetricCard
                                                key={c.id}
                                                infoKey={c.id}
                                                title={c.title}
                                                value={c.value}
                                                caption={c.caption}
                                                highlight={c.highlight}
                                                tipAlign={i === 0 ? 'start' : 'end'}
                                            />
                                        ))}
                                    </div>
                                )}

                                <AccountTableCard
                                    name="setup-d30"
                                    title="Evolução das contas no setup proposto"
                                    columns={SETUP_COLUMNS}
                                    formatters={SETUP_FORMATTERS}
                                    exportFilename="analytics-setup-proposto-d30.xlsx"
                                    emptyMessage="Nenhuma conta com pagamento registrado."
                                    filters={filters}
                                    enabled={queryable}
                                    refreshKey={reloadKey}
                                    timeZone={timeZone}
                                />

                                <AccountTableCard
                                    name="pagou-nunca-acessou"
                                    title="Pagou e nunca acessou"
                                    columns={NEVER_ACCESSED_COLUMNS}
                                    formatters={NEVER_ACCESSED_FORMATTERS}
                                    exportFilename="analytics-pagou-nunca-acessou.xlsx"
                                    emptyMessage="Nenhuma conta pagou e ficou sem acessar."
                                    filters={filters}
                                    enabled={queryable}
                                    refreshKey={reloadKey}
                                    timeZone={timeZone}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </AdminLayout>
    );
}
